import { useEffect, useMemo, useState, useCallback } from 'react'
import { useSelectedCompany } from '../auth/CompanyContext'
import { useYear } from '../auth/YearContext'
import {
  subscribeReceipts,
  deleteReceiptCloud,
  aiAnalyzeReceipt,
  receiptIsMissingVat,
  type CloudReceipt,
} from '../lib/cloudStore'
import { receiptListTitle, type ReceiptCategory } from '../lib/receipts'
import { AiVatBatchDialog } from '../components/AiVatBatchDialog'
import { EditReceiptModal } from '../components/EditReceiptModal'

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun',
  'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec',
]
const fmt = (n: number) => n.toLocaleString('sv-SE', { maximumFractionDigits: 0 })

const CAT_LABEL: Record<ReceiptCategory, string> = {
  kundfakturor: 'Kundfaktura',
  leverantorsfakturor: 'Leverant\u00f6rsfaktura',
  utlagg: 'Utl\u00e4gg',
}

const CAT_COLOR: Record<ReceiptCategory, string> = {
  kundfakturor: '#10b981',
  leverantorsfakturor: '#ef4444',
  utlagg: '#3b82f6',
}

type Sort = 'date-desc' | 'date-asc' | 'amount-desc' | 'amount-asc'


// --- ReceiptDetail (legacy edit-panel) borttagen 2026-05-21 ---
// Ersatt av EditReceiptModal i src/components/EditReceiptModal.tsx



// --- Main Receipts List ---

export function Receipts({ onClose }: { onClose: () => void }) {
  const { selected, companies, setSelectedId } = useSelectedCompany()
  const { year, availableYears, setYear } = useYear()
  const [list, setList] = useState<CloudReceipt[]>([])
  const [err, setErr] = useState<string | null>(null)

  const [catFilter, setCatFilter] = useState<'all' | ReceiptCategory>('all')
  const [monthFilter, setMonthFilter] = useState<'all' | number>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [sort, setSort] = useState<Sort>('date-desc')
  const [selectedReceiptId, setSelectedReceiptId] = useState<string | null>(null)
  // 2026-05-24: toggle-filters för moms-saknande / ofullständiga
  const [onlyMissingVat, setOnlyMissingVat] = useState(false)
  const [onlyIncomplete, setOnlyIncomplete] = useState(false)
  // 2026-05-24: bulk-edit — markerade docIds + öppen action-meny
  const [bulkIds, setBulkIds] = useState<Set<string>>(new Set())
  const [bulkAction, setBulkAction] = useState<null | 'vatRate' | 'accountCode' | 'category'>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [bulkMsg, setBulkMsg] = useState<string | null>(null)

  useEffect(() => {
    setErr(null)
    try {
      return subscribeReceipts(selected.id, year, setList)
    } catch (e) {
      setErr((e as Error).message)
    }
  }, [selected.id, year])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    let arr = list.filter((r) => {
      if (catFilter !== 'all' && r.category !== catFilter) return false
      if (monthFilter !== 'all' && r.month !== monthFilter) return false
      if (q) {
        const hay = [r.filename, r.title, r.date, r.currency].filter(Boolean).join(' ').toLowerCase()
        if (!hay.includes(q)) return false
      }
      if (onlyMissingVat && !receiptIsMissingVat(r)) return false
      if (onlyIncomplete) {
        const incomplete = !r.date || !r.title || r.amount == null || !r.currency
        if (!incomplete) return false
      }
      return true
    })
    // 2026-05-24: safe localeCompare — vissa äldre receipts har createdAt som
    // number eller saknar fältet. Konvertera till string först annars kraschar
    // hela sidan med "X.localeCompare is not a function".
    const safeStr = (v: unknown): string => v == null ? '' : String(v)
    arr = arr.sort((a, b) => {
      switch (sort) {
        case 'date-asc':   return safeStr(a.date).localeCompare(safeStr(b.date)) || safeStr(a.createdAt).localeCompare(safeStr(b.createdAt))
        case 'date-desc':  return safeStr(b.date).localeCompare(safeStr(a.date)) || safeStr(b.createdAt).localeCompare(safeStr(a.createdAt))
        case 'amount-asc': return (a.amount ?? 0) - (b.amount ?? 0)
        case 'amount-desc':return (b.amount ?? 0) - (a.amount ?? 0)
      }
    })
    return arr
  }, [list, catFilter, monthFilter, searchQuery, sort, onlyMissingVat, onlyIncomplete])

  const sums = useMemo(() => {
    const s: Record<string, number> = { all: 0, kundfakturor: 0, leverantorsfakturor: 0, utlagg: 0 }
    for (const r of filtered) {
      const v = r.amount ?? 0
      s.all += v
      s[r.category] += v
    }
    return s
  }, [filtered])

  const incompleteCount = useMemo(() => {
    return filtered.filter(r => !r.date || !r.title || r.amount == null || !r.currency).length
  }, [filtered])

  // FAS B (2026-05-21): poster i aktuellt bolag som saknar moms-data
  const missingVatList = useMemo(
    () => list.filter((r) => receiptIsMissingVat(r)),
    [list]
  )
  const [showAiVatBatch, setShowAiVatBatch] = useState(false)

  const [analyzing, setAnalyzing] = useState<string | null>(null)

  async function onAnalyze(r: CloudReceipt) {
    setAnalyzing(r.id)
    try {
      const res = await aiAnalyzeReceipt(selected.id, r)
      if (res.skipped) {
        // Already complete
      } else if (!res.updated) {
        alert('AI: ' + (res.error ?? 'ingen uppdatering'))
      }
    } finally {
      setAnalyzing(null)
    }
  }

  async function onDelete(r: CloudReceipt) {
    if (!confirm('Radera ' + r.filename + '?\n\nFil tas bort fr\u00e5n Firebase Storage och post fr\u00e5n Firestore.')) return
    try {
      await deleteReceiptCloud(selected.id, r)
    } catch (e) {
      alert('Fel: ' + (e as Error).message)
    }
  }

  // -------- CSV export (2026-05-24) --------
  function csvEscape(v: unknown): string {
    if (v == null) return ''
    const s = String(v)
    if (/[";\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"'
    return s
  }

  function exportCsv() {
    const headers = [
      'docId', 'datum', 'kategori', 'beskrivning', 'filnamn',
      'amount', 'amountExVat', 'vatAmount', 'vatRate', 'valuta',
      'accountCode', 'accountLabel', 'vatStatus', 'quality', 'year', 'month',
    ]
    const lines: string[] = [headers.join(';')]
    for (const r of filtered) {
      lines.push([
        r.id,
        r.date ?? '',
        r.category,
        r.title ?? '',
        r.filename,
        r.amount ?? '',
        r.amountExVat ?? '',
        r.vatAmount ?? '',
        r.vatRate != null ? r.vatRate.toString().replace('.', ',') : '',
        r.currency ?? '',
        r.accountCode ?? '',
        r.accountLabel ?? '',
        r.vatStatus ?? '',
        r.quality ?? '',
        r.year,
        r.month,
      ].map(csvEscape).join(';'))
    }
    // BOM för att Excel ska tolka som UTF-8 + svensk filnamnskonvention
    const bom = '﻿'
    const csv = bom + lines.join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const ts = new Date().toISOString().slice(0, 10)
    a.download = ts + ' - ' + selected.id + ' - ' + year + ' - kvitton.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  // -------- Bulk-edit (2026-05-24) --------
  async function runBulkUpdate(updates: Array<Record<string, unknown>>) {
    setBulkBusy(true)
    setBulkMsg(null)
    try {
      const { getAuth } = await import('firebase/auth')
      const user = getAuth().currentUser
      if (!user) throw new Error('Ej inloggad')
      const idToken = await user.getIdToken()
      const res = await fetch('/api/bulk-set-vat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + idToken,
        },
        body: JSON.stringify({ uid: user.uid, companyId: selected.id, updates }),
      })
      const data = await res.json() as { ok: boolean; updated?: number; failed?: number; error?: string }
      if (data.ok) {
        setBulkMsg('✓ Uppdaterade ' + data.updated + (data.failed ? ' (' + data.failed + ' misslyckades)' : ''))
        setBulkIds(new Set())
        setBulkAction(null)
      } else {
        setBulkMsg('Fel: ' + (data.error || 'okänt'))
      }
    } catch (e) {
      setBulkMsg('Fel: ' + (e as Error).message)
    } finally {
      setBulkBusy(false)
      setTimeout(() => setBulkMsg(null), 4000)
    }
  }

  async function bulkSetVatRate(rate: number) {
    const updates = Array.from(bulkIds).map(docId => ({ docId, vatRate: rate }))
    await runBulkUpdate(updates)
  }
  async function bulkSetAccountCode(code: number) {
    const updates = Array.from(bulkIds).map(docId => ({ docId, accountCode: code }))
    await runBulkUpdate(updates)
  }
  async function bulkSetCategory(cat: ReceiptCategory) {
    const updates = Array.from(bulkIds).map(docId => ({ docId, category: cat }))
    await runBulkUpdate(updates)
  }
  async function bulkDelete() {
    if (!confirm('Radera ' + bulkIds.size + ' markerade poster?\n\nFiler tas bort från Storage och poster från Firestore.')) return
    setBulkBusy(true)
    let ok = 0, failed = 0
    for (const id of bulkIds) {
      const r = list.find(x => x.id === id)
      if (!r) { failed++; continue }
      try { await deleteReceiptCloud(selected.id, r); ok++ } catch { failed++ }
    }
    setBulkMsg('✓ Raderade ' + ok + (failed ? ' (' + failed + ' misslyckades)' : ''))
    setBulkIds(new Set())
    setBulkAction(null)
    setBulkBusy(false)
    setTimeout(() => setBulkMsg(null), 4000)
  }

  function toggleBulkId(id: string) {
    setBulkIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }
  function toggleAllBulk() {
    if (bulkIds.size === filtered.length) {
      setBulkIds(new Set())
    } else {
      setBulkIds(new Set(filtered.map(r => r.id)))
    }
  }

  // Rensa markeringar när filter ändras
  useEffect(() => { setBulkIds(new Set()) }, [catFilter, monthFilter, searchQuery, onlyMissingVat, onlyIncomplete])

  // Batch AI
  const [batchRunning, setBatchRunning] = useState(false)
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0, skipped: 0 })

  const runBatchAI = useCallback(async () => {
    const incomplete = filtered.filter(r => !r.date || !r.title || r.amount == null || !r.currency)
    if (incomplete.length === 0) return
    setBatchRunning(true)
    setBatchProgress({ done: 0, total: incomplete.length, skipped: 0 })
    let skipped = 0
    for (let i = 0; i < incomplete.length; i++) {
      try {
        const res = await aiAnalyzeReceipt(selected.id, incomplete[i])
        if (res.skipped) skipped++
      } catch { /* continue */ }
      setBatchProgress({ done: i + 1, total: incomplete.length, skipped })
    }
    setBatchRunning(false)
  }, [filtered, selected.id])

  const selectedReceipt = selectedReceiptId ? list.find(r => r.id === selectedReceiptId) ?? null : null

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar__brand">Kvitton</div>
        <div className="topbar__right">
          <select
            value={selected.id}
            onChange={(e) => setSelectedId(e.target.value)}
            style={selectStyle}
          >
            {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            style={selectStyle}
          >
            {availableYears.map((y) => (<option key={y} value={y}>{'\u00c5r ' + y}</option>))}
          </select>
          <button className="logout-btn" onClick={onClose}>{'\u2190 Tillbaka'}</button>
        </div>
      </header>

      {err && <div style={errBoxStyle}>{err}</div>}

      {/* Filter */}
      <section className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
          <div>
            <label style={labelStyle}>Kategori</label>
            <select value={catFilter} onChange={(e) => setCatFilter(e.target.value as any)} style={{ ...selectStyle, width: '100%' }}>
              <option value="all">Alla</option>
              <option value="kundfakturor">Kundfakturor</option>
              <option value="leverantorsfakturor">{`Leverant\u00f6rsfakturor`}</option>
              <option value="utlagg">{`Utl\u00e4gg`}</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>{`M\u00e5nad`}</label>
            <select value={monthFilter} onChange={(e) => setMonthFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))} style={{ ...selectStyle, width: '100%' }}>
              <option value="all">{`Alla m\u00e5nader`}</option>
              {MONTHS.map((m, i) => (<option key={m} value={i}>{m}</option>))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Sortera</label>
            <select value={sort} onChange={(e) => setSort(e.target.value as Sort)} style={{ ...selectStyle, width: '100%' }}>
              <option value="date-desc">{`Datum \u2014 nyast f\u00f6rst`}</option>
              <option value="date-asc">{`Datum \u2014 \u00e4ldst f\u00f6rst`}</option>
              <option value="amount-desc">{`Belopp \u2014 h\u00f6gst f\u00f6rst`}</option>
              <option value="amount-asc">{`Belopp \u2014 l\u00e4gst f\u00f6rst`}</option>
            </select>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>{`S\u00f6k`}</label>
            <input
              type="search"
              placeholder={`Filnamn, beskrivning, valuta\u2026`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                background: 'var(--bg-card-alt)',
                color: 'var(--text)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                padding: '0.5rem 0.7rem',
                fontSize: '0.95rem',
                width: '100%',
              }}
            />
          </div>
        </div>

        {/* Toggle-filters (2026-05-24): saknar moms / ofullständiga */}
        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.8rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setOnlyMissingVat(v => !v)}
            style={{
              ...selectStyle,
              background: onlyMissingVat ? 'rgba(245,158,11,0.2)' : 'var(--bg-card-alt)',
              border: '1px solid ' + (onlyMissingVat ? '#f59e0b' : 'var(--border)'),
              color: onlyMissingVat ? '#f59e0b' : 'var(--text)',
              cursor: 'pointer',
              fontWeight: onlyMissingVat ? 600 : 400,
            }}
          >
            {(onlyMissingVat ? '✓ ' : '') + 'Endast saknar moms (' + list.filter(receiptIsMissingVat).length + ')'}
          </button>
          <button
            type="button"
            onClick={() => setOnlyIncomplete(v => !v)}
            style={{
              ...selectStyle,
              background: onlyIncomplete ? 'rgba(245,158,11,0.2)' : 'var(--bg-card-alt)',
              border: '1px solid ' + (onlyIncomplete ? '#f59e0b' : 'var(--border)'),
              color: onlyIncomplete ? '#f59e0b' : 'var(--text)',
              cursor: 'pointer',
              fontWeight: onlyIncomplete ? 600 : 400,
            }}
          >
            {(onlyIncomplete ? '✓ ' : '') + 'Endast ofullständiga (' + list.filter(r => !r.date || !r.title || r.amount == null || !r.currency).length + ')'}
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={exportCsv}
            disabled={filtered.length === 0}
            style={{
              ...selectStyle,
              cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
              background: 'var(--bg-card-alt)',
              opacity: filtered.length === 0 ? 0.5 : 1,
            }}
            title={'Exportera nuvarande filter (' + filtered.length + ' st) som CSV som Excel öppnar'}
          >
            {'📥 Exportera ' + filtered.length + ' till CSV'}
          </button>
        </div>

        {/* Summering */}
        <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginTop: '1rem', fontSize: '0.9rem', alignItems: 'center' }}>
          <div>
            <span style={{ color: 'var(--text-muted)' }}>Totalt ({filtered.length} st):</span>{' '}
            <b>{fmt(sums.all)}</b>
          </div>
          <div>
            <span style={{ color: CAT_COLOR.kundfakturor }}>{'\u25cf '}</span>
            <span style={{ color: 'var(--text-muted)' }}>{`Int\u00e4kt:`}</span>{' '}
            <b>{fmt(sums.kundfakturor)}</b>
          </div>
          <div>
            <span style={{ color: CAT_COLOR.leverantorsfakturor }}>{'\u25cf '}</span>
            <span style={{ color: 'var(--text-muted)' }}>Kostnad:</span>{' '}
            <b>{fmt(sums.leverantorsfakturor)}</b>
          </div>
          <div>
            <span style={{ color: CAT_COLOR.utlagg }}>{'\u25cf '}</span>
            <span style={{ color: 'var(--text-muted)' }}>{`Utl\u00e4gg:`}</span>{' '}
            <b>{fmt(sums.utlagg)}</b>
          </div>
          {missingVatList.length > 0 && (
            <>
              <span style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  background: 'rgba(245,158,11,0.15)',
                  color: 'var(--warning)',
                  fontSize: '0.75rem',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontWeight: 600,
                }}>
                  {missingVatList.length + ' saknar moms'}
                </span>
                <button
                  className="logout-btn"
                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                  onClick={() => setShowAiVatBatch(true)}
                  title={`Kör AI-moms-analys på poster som saknar moms-data`}
                >{'🤖 AI-moms-analys'}</button>
              </div>
            </>
          )}
          {incompleteCount > 0 && (
            <>
              <span style={{ flex: 1 }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{
                  background: 'rgba(245,158,11,0.15)',
                  color: 'var(--warning)',
                  fontSize: '0.75rem',
                  padding: '2px 8px',
                  borderRadius: 4,
                  fontWeight: 600,
                }}>
                  {incompleteCount} {`ofullst\u00e4ndiga`}
                </span>
                <button
                  className="logout-btn"
                  style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem' }}
                  onClick={runBatchAI}
                  disabled={batchRunning}
                  title={`K\u00f6r AI-analys p\u00e5 alla kvitton som saknar f\u00e4lt`}
                >
                  {batchRunning
                    ? `AI ${batchProgress.done}/${batchProgress.total}\u2026`
                    : '\ud83e\udde0 AI-fyll alla'
                  }
                </button>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Bulk action bar (2026-05-24): visas när minst en rad är markerad */}
      {bulkIds.size > 0 && (
        <section className="card" style={{ marginBottom: '1rem', padding: '0.7rem 1rem', background: 'rgba(59,130,246,0.08)', border: '1px solid #3b82f6' }}>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <strong style={{ color: '#3b82f6' }}>{bulkIds.size + ' markerade'}</strong>
            <span style={{ flex: 1 }} />
            <select
              disabled={bulkBusy}
              value={bulkAction === 'vatRate' ? 'vatRate' : ''}
              onChange={(e) => setBulkAction(e.target.value as 'vatRate' || null)}
              style={selectStyle}
            >
              <option value="">Välj action…</option>
              <option value="vatRate">Sätt momssats</option>
              <option value="accountCode">Sätt kontokod</option>
              <option value="category">Byt kategori</option>
            </select>
            {bulkAction === 'vatRate' && (
              <>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetVatRate(0)}>0% (reverse charge)</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetVatRate(0.06)}>6%</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetVatRate(0.12)}>12%</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetVatRate(0.25)}>25%</button>
              </>
            )}
            {bulkAction === 'accountCode' && (
              <>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetAccountCode(3010)}>3010 Försäljning</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetAccountCode(5410)}>5410 Förbr.inv</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetAccountCode(6230)}>6230 IT/tele</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetAccountCode(6990)}>6990 Övr ext</button>
              </>
            )}
            {bulkAction === 'category' && (
              <>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetCategory('kundfakturor')}>Kundfaktura</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetCategory('leverantorsfakturor')}>Lev.faktura</button>
                <button className="logout-btn" disabled={bulkBusy} onClick={() => bulkSetCategory('utlagg')}>Utlägg</button>
              </>
            )}
            <button
              className="logout-btn"
              disabled={bulkBusy}
              onClick={bulkDelete}
              style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', border: '1px solid #ef4444' }}
            >
              {'🗑 Radera markerade'}
            </button>
            <button
              className="logout-btn"
              disabled={bulkBusy}
              onClick={() => { setBulkIds(new Set()); setBulkAction(null) }}
            >
              Avbryt
            </button>
          </div>
          {bulkMsg && (
            <div style={{ marginTop: 6, fontSize: '0.85rem', color: bulkMsg.startsWith('✓') ? 'var(--success)' : 'var(--danger)' }}>
              {bulkMsg}
            </div>
          )}
        </section>
      )}

      {/* Tabell */}
      <section className="card">
        {filtered.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            Inga kvitton matchar filtret. Prova att rensa filter eller importera via Import.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 32 }} title="Markera alla">
                    <input
                      type="checkbox"
                      checked={bulkIds.size === filtered.length && filtered.length > 0}
                      ref={el => { if (el) el.indeterminate = bulkIds.size > 0 && bulkIds.size < filtered.length }}
                      onChange={toggleAllBulk}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={thStyle}>Datum</th>
                  <th style={thStyle}>Kategori</th>
                  <th style={thStyle}>Beskrivning / filnamn</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Belopp</th>
                  <th style={thStyle}>Valuta</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const incomplete = !r.date || !r.title || r.amount == null || !r.currency
                  return (
                    <tr
                      key={r.id}
                      style={{
                        borderTop: '1px solid var(--border)',
                        cursor: 'pointer',
                        background: bulkIds.has(r.id)
                          ? 'rgba(59,130,246,0.12)'
                          : selectedReceiptId === r.id ? 'rgba(59,130,246,0.08)' : undefined,
                      }}
                      onClick={() => setSelectedReceiptId(r.id)}
                      title="Klicka f&ouml;r att redigera"
                    >
                      <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={bulkIds.has(r.id)}
                          onChange={() => toggleBulkId(r.id)}
                          style={{ cursor: 'pointer' }}
                        />
                      </td>
                      <td style={tdStyle}>{r.date ?? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{MONTHS[r.month]}</span>}</td>
                      <td style={tdStyle}>
                        <span style={{ color: CAT_COLOR[r.category] }}>{'\u25cf'}</span>{' '}
                        <span style={{ fontSize: '0.8rem' }}>{CAT_LABEL[r.category]}</span>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.filename}>
                        {receiptListTitle(r)}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {r.amount != null ? fmt(r.amount) : <span style={{ color: 'var(--text-muted)' }}>{'\u2014'}</span>}
                      </td>
                      <td style={tdStyle}>{r.currency ?? ''}</td>
                      <td style={tdStyle}>
                        {incomplete ? (
                          <span style={{
                            background: 'rgba(245,158,11,0.15)',
                            color: 'var(--warning)',
                            fontSize: '0.65rem',
                            padding: '2px 6px',
                            borderRadius: 4,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                          }}>
                            {`Ofullst\u00e4ndig`}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--success)', fontSize: '0.65rem', fontWeight: 600 }}>OK</span>
                        )}
                      </td>
                      <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                        <button
                          className="logout-btn"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', marginRight: 4 }}
                          onClick={() => setSelectedReceiptId(r.id)}
                          title="Redigera alla f\u00e4lt manuellt"
                        >
                          {'\u270e Redigera'}
                        </button>
                        <button
                          className="logout-btn"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem', marginRight: 4 }}
                          onClick={() => onAnalyze(r)}
                          disabled={analyzing === r.id}
                          title={`L\u00e4s med AI och fyll i saknade f\u00e4lt`}
                        >
                          {analyzing === r.id ? '\u2026' : '\ud83e\udde0 AI'}
                        </button>
                        <button
                          className="logout-btn"
                          style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}
                          onClick={() => onDelete(r)}
                        >
                          Ta bort
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Edit-modal (2026-05-21: ersätter gamla ReceiptDetail) */}
      {selectedReceipt && (
        <EditReceiptModal
          receipt={selectedReceipt}
          companyId={selected.id}
          onClose={() => setSelectedReceiptId(null)}
          onDeleted={() => setSelectedReceiptId(null)}
        />
      )}

      {/* FAS B (2026-05-21): AI-moms-batch-dialog */}
      <AiVatBatchDialog
        open={showAiVatBatch}
        onClose={() => setShowAiVatBatch(false)}
        companyId={selected.id}
        companyName={selected.name}
        receipts={missingVatList}
      />
    </div>
  )
}

// --- Styles ---

const selectStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  color: 'var(--text)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  padding: '0.4rem 0.7rem',
  fontSize: '0.9rem',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.7rem',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--text-muted)',
  marginBottom: 4,
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  fontWeight: 600,
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
}

const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.6rem',
  verticalAlign: 'middle',
}

const errBoxStyle: React.CSSProperties = {
  background: 'rgba(239,68,68,0.1)',
  border: '1px solid var(--danger)',
  color: 'var(--danger)',
  padding: '0.5rem 1rem',
  borderRadius: 8,
  marginBottom: '1rem',
}
